import { useState, useEffect, useRef, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { Toast } from 'primereact/toast';

import { useUser } from '../../contexts/UserContext';
import { useToast } from '../../contexts/ToastContext';
import { useConfirmationDialog } from '../../utils/ConfirmationDialogContext';
import { useSpinner } from '../../utils/GlobalSpinner';
import { api } from '../../services/api-client';
import { formatDate, getSeverity } from '../../utils/UtilFunctions';
import { WorkoutStatus } from '../../types/enums/workout-status';

interface ISelectOption {
  label: string;
  value: string;
}

interface IWorkoutInstance {
  id?: number;
  status: WorkoutStatus;
  realEndDate?: string;
  workout: { planName: string };
  trainingSession: {
    sessionDate: string | Date;
    dayNumber?: number;
    trainingWeek?: { weekNumber?: number };
  };
}

interface IPersonalInfo {
  id: number;
  name: string;
  birthdate: string | null;
  gender: string | null;
  phoneNumber: number | null;
  activityLevel: string;
  fitnessGoal: string;
  user?: { email: string };
}

type PaymentFrequency = 'monthly' | 'weekly' | 'per_session';

interface ISubscriptionData {
  coachPlan?: { id: number; name: string; paymentFrequency?: PaymentFrequency };
  subscription?: {
    startDate: string;
    endDate: string | null;
    status: string;
  };
  workoutInstances: IWorkoutInstance[];
}

interface IStreak {
  currentStreak: number;
}

interface IStreakData {
  daily: IStreak | null;
  weekly: IStreak | null;
}

interface ITodayTraining {
  workoutInstanceId: number;
  planName: string;
  status: string;
}

export const useClientProfile = () => {
  const { user, client } = useUser();
  const { showToast } = useToast();
  const { showConfirmationDialog } = useConfirmationDialog();
  const { setLoading } = useSpinner();
  const navigate = useNavigate();
  const toast = useRef<Toast>(null);
  const intl = useIntl();

  const [personalInfo, setPersonalInfo] = useState<IPersonalInfo | null>(null);
  const [subscription, setSubscription] = useState<ISubscriptionData | null>(null);
  const [streak, setStreak] = useState<IStreakData>({ daily: null, weekly: null });
  const [todayTraining, setTodayTraining] = useState<ITodayTraining | null>(null);

  const [editDialogVisible, setEditDialogVisible] = useState(false);
  const [editName, setEditName] = useState<string>('');
  const [editBirthdate, setEditBirthdate] = useState<Date | null>(null);
  const [editGender, setEditGender] = useState<string>('');
  const [editPhoneNumber, setEditPhoneNumber] = useState<number | null>(null);
  const [fitnessGoal, setFitnessGoal] = useState<string[]>([]);
  const [activityLevel, setActivityLevel] = useState<string>('');

  const [refreshKey, setRefreshKey] = useState(0);
  const [isPaymentDialogVisible, setIsPaymentDialogVisible] = useState(false);

  useEffect(() => {
    if (!user) return;

    const fetchData = async () => {
      try {
        setLoading(true);

        const [profileRes, subRes, streakRes, todayRes] = await Promise.allSettled([
          api.user.fetchMyClientProfile(),
          api.subscription.fetchMySubscriptionDetails(),
          api.user.fetchMyStreak(),
          api.user.fetchAmITrainingToday()
        ]);

        if (profileRes.status === 'fulfilled') {
          const data = profileRes.value.data as IPersonalInfo;
          setPersonalInfo(data);
          setActivityLevel(data.activityLevel || '');
          if (data.fitnessGoal) {
            const goals = data.fitnessGoal
              .split(',')
              .map((g: string) => g.trim())
              .filter((v: string, i: number, self: string[]) => self.indexOf(v) === i);
            setFitnessGoal(goals);
          }
        }

        if (subRes.status === 'fulfilled') {
          setSubscription(subRes.value.data as ISubscriptionData);
        }

        if (streakRes.status === 'fulfilled' && streakRes.value.data) {
          setStreak(streakRes.value.data as IStreakData);
        }

        if (todayRes.status === 'fulfilled') {
          const list = (todayRes.value.data || []) as Array<Record<string, unknown>>;
          const first = list[0];
          if (first) {
            setTodayTraining({
              workoutInstanceId: Number(first.workoutInstanceId),
              planName: String(first.planName || ''),
              status: String(first.status || '')
            });
          } else {
            setTodayTraining(null);
          }
        }
      } catch (error) {
        showToast('error', 'Error', (error as Error).message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user, showToast, setLoading, refreshKey]);

  const recentHistory = useMemo<IWorkoutInstance[]>(() => {
    const items = subscription?.workoutInstances || [];
    const sorted = [...items].sort((a, b) => {
      const da = new Date(a.trainingSession.sessionDate).getTime();
      const db = new Date(b.trainingSession.sessionDate).getTime();
      return db - da;
    });
    return sorted.slice(0, 5);
  }, [subscription]);

  const paymentFrequency = subscription?.coachPlan?.paymentFrequency ?? null;
  const isPerSession = paymentFrequency === 'per_session';

  const daysUntilExpiry = useMemo<number | null>(() => {
    if (isPerSession) return null;
    const end = subscription?.subscription?.endDate;
    if (!end) return null;
    const endDate = new Date(end);
    const today = new Date();
    endDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    const diffMs = endDate.getTime() - today.getTime();
    return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  }, [subscription, isPerSession]);

  const sessionsUsed = useMemo<number>(() => {
    return subscription?.workoutInstances?.length ?? 0;
  }, [subscription]);

  const subscriptionStatusSeverity = useMemo<'success' | 'danger' | 'warning'>(() => {
    const status = (subscription?.subscription?.status || '').toLowerCase();
    if (status === 'active') return 'success';
    if (status === 'expired' || status === 'inactive') return 'danger';
    return 'warning';
  }, [subscription]);

  const handleEditPersonalInfo = () => {
    if (!personalInfo) {
      showToast('warn', intl.formatMessage({ id: 'profile.toast.noData' }), '');
      return;
    }
    setEditName(personalInfo.name || '');
    setEditBirthdate(personalInfo.birthdate ? new Date(personalInfo.birthdate) : null);
    setEditGender(personalInfo.gender || '');
    setEditPhoneNumber(personalInfo.phoneNumber);
    setEditDialogVisible(true);
  };

  const handleEditDialogClose = () => {
    setEditDialogVisible(false);
  };

  const handleSavePersonalInfo = async () => {
    if (!personalInfo) {
      showToast('error', 'Error', intl.formatMessage({ id: 'profile.toast.noData' }));
      return;
    }

    if (fitnessGoal.length === 0) {
      showToast('error', 'Error', intl.formatMessage({ id: 'profile.validation.fitnessGoal' }));
      return;
    }
    if (!activityLevel) {
      showToast('error', 'Error', intl.formatMessage({ id: 'profile.validation.activityLevel' }));
      return;
    }

    const body: Record<string, unknown> = {
      fitnessGoal,
      activityLevel
    };
    if (editName.trim()) body.name = editName.trim();
    if (editBirthdate) body.birthdate = editBirthdate;
    if (editGender) body.gender = editGender;
    if (editPhoneNumber != null) body.phoneNumber = String(editPhoneNumber);

    showConfirmationDialog({
      message: intl.formatMessage({ id: 'common.confirmation.saveChanges' }),
      header: intl.formatMessage({ id: 'common.confirmation' }),
      icon: 'pi pi-exclamation-triangle',
      accept: async () => {
        try {
          setLoading(true);
          // #35: endpoint self-service. El anterior (/users/client/:id) es
          // @Roles(COACH) y le devolvía 403 a todo alumno que intentara
          // guardar su perfil desde la web.
          await api.user.updateMyClientProfile(body);
          showToast('success', 'Success', intl.formatMessage({ id: 'profile.toast.updated' }));
          setEditDialogVisible(false);
          setRefreshKey((prev) => prev + 1);
        } catch (error) {
          showToast('error', 'Error', (error as Error).message);
        } finally {
          setLoading(false);
        }
      },
      reject: () => {
        /* no-op */
      }
    });
  };

  const handleOpenPaymentDialog = () => setIsPaymentDialogVisible(true);
  const handlePaymentDialogClose = () => {
    setIsPaymentDialogVisible(false);
    setRefreshKey((prev) => prev + 1);
  };

  const handleStartTraining = () => {
    if (!todayTraining) return;
    navigate(`/plans/start-session/${todayTraining.workoutInstanceId}`, {
      state: { isTraining: true, planId: todayTraining.workoutInstanceId }
    });
  };

  const handleViewTodayDetails = () => {
    if (!todayTraining) return;
    navigate(`/plans/start-session/${todayTraining.workoutInstanceId}`, {
      state: { isTraining: false, planId: todayTraining.workoutInstanceId }
    });
  };

  const isTodayCompleted = (todayTraining?.status || '').toLowerCase() === 'completed';

  const handleContactCoach = () => {
    const coach = client?.coach;
    if (!coach) return;
    if (coach.email) {
      window.location.href = `mailto:${coach.email}`;
    }
  };

  const fitnessGoalOptions: ISelectOption[] = [
    { label: intl.formatMessage({ id: 'profile.goals.weightLoss' }), value: 'weight loss' },
    { label: intl.formatMessage({ id: 'profile.goals.muscleGain' }), value: 'muscle gain' },
    { label: intl.formatMessage({ id: 'profile.goals.mobility' }), value: 'gain mobility' },
    { label: intl.formatMessage({ id: 'profile.goals.maintenance' }), value: 'maintenance' },
    { label: intl.formatMessage({ id: 'profile.goals.flexibility' }), value: 'flexibility' }
  ];

  const activityLevelOptions: ISelectOption[] = [
    { label: intl.formatMessage({ id: 'profile.activity.sedentary' }), value: 'sedentary' },
    { label: intl.formatMessage({ id: 'profile.activity.moderate' }), value: 'moderately active' },
    { label: intl.formatMessage({ id: 'profile.activity.active' }), value: 'very active' }
  ];

  const genderOptions: ISelectOption[] = [
    { label: intl.formatMessage({ id: 'gender.male' }), value: 'Male' },
    { label: intl.formatMessage({ id: 'gender.female' }), value: 'Female' },
    { label: intl.formatMessage({ id: 'gender.other' }), value: 'Other' }
  ];

  return {
    intl,
    client,
    toast,

    personalInfo,
    subscription,
    streak,
    todayTraining,
    recentHistory,
    daysUntilExpiry,
    paymentFrequency,
    isPerSession,
    sessionsUsed,
    subscriptionStatusSeverity,

    editDialogVisible,
    editName,
    setEditName,
    editBirthdate,
    setEditBirthdate,
    editGender,
    setEditGender,
    editPhoneNumber,
    setEditPhoneNumber,
    fitnessGoal,
    setFitnessGoal,
    activityLevel,
    setActivityLevel,

    isPaymentDialogVisible,

    handleEditPersonalInfo,
    handleEditDialogClose,
    handleSavePersonalInfo,
    handleOpenPaymentDialog,
    handlePaymentDialogClose,
    handleStartTraining,
    handleViewTodayDetails,
    isTodayCompleted,
    handleContactCoach,

    fitnessGoalOptions,
    activityLevelOptions,
    genderOptions,

    formatDate,
    getSeverity
  };
};
